import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '@/components/PageHero';
import FadeInUp from '@/components/FadeInUp';
import { CheckIcon, ClockIcon, ShieldIcon, CertificateIcon, ArrowRight, UsersIcon, HardHatIcon, ForkliftIcon } from '@/components/icons';

const STEPS = [
  { n: 1, title: 'Introduction', text: 'Welcome, briefing, and agenda overview.' },
  { n: 2, title: 'Video Presentation', text: 'WorkSafeNZ-aligned content covering operator best practice.' },
  { n: 3, title: 'Quiz on Video', text: 'Confirms key safety concepts have landed.' },
  { n: 4, title: 'Theory Test (Parts 1 & 2)', text: 'Written assessment covering operations, hazards & compliance.' },
  { n: 5, title: 'Test Results', text: 'Immediate feedback. Trainees know exactly where they stand.' },
  { n: 6, title: 'Pre-Operation Checks', text: 'Hands-on demonstration of daily inspection routine.' },
  { n: 7, title: 'Driving Test', text: 'Practical assessment per machine, observed by NFT trainer.' },
  { n: 8, title: 'Summary', text: 'Recap of what was covered, certification issuance, Q&A.' },
];

const EQUIPMENT = [
  'Ride-on Counterbalance Forklifts', 'Reach Trucks', 'Battery Electric Units',
  'Internal Combustion Units', 'Walk-behind Units', 'Powered Pallet Trucks',
  'Hand Pallet Trucks', 'Stock Pickers', 'Order Pickers',
  'Container Handling Equipment', 'Turret Machines', 'And many more',
];

export default function StandardCourse() {
  return (
    <>
      <PageHero
        eyebrow="Standard / Renewal Course"
        title="For new (but experienced) operators & renewals."
        subtitle="WorkSafeNZ approved. Photo ID licence card. Audited by Competenz. Valid for three years."
        image="/generated/course-standard.png"
        breadcrumbs={[{label:'Home', to:'/'}, {label:'Courses', to:'/courses'}, {label:'Standard / Renewal'}]}
        badge="WorkSafeNZ Approved"
      />

      <section className="nft-container py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Sticky summary */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-32 surface-card p-6">
              <div className="label-eyebrow text-[#FC4E2C] mb-2">Course Summary</div>
              <h2 className="font-display font-extrabold uppercase text-2xl text-[#060D9C] leading-tight">Standard / Renewal</h2>
              <ul className="mt-5 space-y-3 text-sm" data-testid="standard-course-summary">
                <li className="flex items-start gap-2">
                  <ClockIcon width={16} height={16} className="shrink-0 mt-0.5 text-[#FC4E2C]" />
                  <span><strong>Theory:</strong> ~4.5 hrs &bull; <strong>Driving:</strong> 15–20 min/person/machine &bull; <strong>Summary:</strong> ~30 min</span>
                </li>
                <li className="flex items-start gap-2">
                  <UsersIcon width={16} height={16} className="shrink-0 mt-0.5 text-[#FC4E2C]" />
                  <span><strong>Max class size:</strong> 10 trainees</span>
                </li>
                <li className="flex items-start gap-2">
                  <HardHatIcon width={16} height={16} className="shrink-0 mt-0.5 text-[#FC4E2C]" />
                  <span>On-site uses your equipment; theory needs only a room with power</span>
                </li>
                <li className="flex items-start gap-2">
                  <ShieldIcon width={16} height={16} className="shrink-0 mt-0.5 text-[#FC4E2C]" />
                  <span>Only competent drivers are issued a certificate</span>
                </li>
              </ul>
              <div className="mt-6 pt-6 border-t border-[#060D9C]/10">
                <Link to="/booking" className="btn-primary-orange w-full justify-center" data-testid="standard-course-book-cta">
                  Book this course <ArrowRight width={16} height={16} />
                </Link>
              </div>
              <p className="mt-3 text-xs text-[#060D9C]/60 text-center">
                WINZ quotations available on request.
              </p>
            </div>
          </aside>

          {/* Content */}
          <div className="lg:col-span-8 space-y-12">
            {/* Format */}
            <FadeInUp>
              <div>
                <div className="label-eyebrow text-[#FC4E2C] mb-2">Course Format</div>
                <h2 className="font-display font-extrabold uppercase text-3xl lg:text-4xl text-[#060D9C] leading-tight">When & where you train.</h2>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="callout-steel">
                    <div className="label-eyebrow text-[#060D9C]/70 mb-1">On-Site (Group)</div>
                    <p className="text-sm text-[#060D9C]/85">7 days a week, 24 hours a day. Almost anywhere in New Zealand. We come to you. Flexible start times to suit client.</p>
                  </div>
                  <div className="callout-steel">
                    <div className="label-eyebrow text-[#060D9C]/70 mb-1">At NFT (East Tamaki)</div>
                    <p className="text-sm text-[#060D9C]/85">Mondays, Wednesdays, Thursdays & Fridays, plus 1x Saturday per month.</p>
                  </div>
                </div>
              </div>
            </FadeInUp>

            {/* Steps */}
            <FadeInUp>
              <div data-testid="standard-course-steps">
                <div className="label-eyebrow text-[#FC4E2C] mb-2">Course Structure</div>
                <h2 className="font-display font-extrabold uppercase text-3xl lg:text-4xl text-[#060D9C] leading-tight">Eight steps to certification.</h2>
                <ol className="mt-6 space-y-3">
                  {STEPS.map((s) => (
                    <li key={s.n} className="flex items-start gap-4 surface-card p-4">
                      <div className="shrink-0 w-10 h-10 rounded-lg bg-[#060D9C] text-white font-mono-cert font-bold flex items-center justify-center text-sm">
                        {String(s.n).padStart(2, '0')}
                      </div>
                      <div>
                        <h3 className="font-display font-extrabold uppercase text-lg text-[#060D9C] leading-tight">{s.title}</h3>
                        <p className="text-sm text-[#060D9C]/75 mt-1">{s.text}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </FadeInUp>

            {/* Equipment */}
            <FadeInUp>
              <div>
                <div className="label-eyebrow text-[#FC4E2C] mb-2">Equipment Covered</div>
                <h2 className="font-display font-extrabold uppercase text-3xl lg:text-4xl text-[#060D9C] leading-tight">Theory covers a wide range.</h2>
                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {EQUIPMENT.map((e) => (
                    <li key={e} className="flex items-center gap-2 text-sm text-[#060D9C]/85">
                      <span className="shrink-0 w-6 h-6 rounded bg-[#FC4E2C]/15 text-[#FC4E2C] flex items-center justify-center">
                        <ForkliftIcon width={14} height={14} />
                      </span>
                      {e}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeInUp>

            {/* Certification */}
            <FadeInUp>
              <div className="surface-card p-6">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-xl bg-[#2D8653]/15 text-[#2D8653] flex items-center justify-center">
                    <CertificateIcon width={26} height={26} />
                  </div>
                  <div>
                    <div className="label-eyebrow text-[#FC4E2C] mb-1">Certification</div>
                    <h3 className="font-display font-extrabold uppercase text-2xl text-[#060D9C] leading-tight">WorkSafeNZ approved · Photo ID licence card.</h3>
                    <ul className="mt-4 space-y-2 text-sm text-[#060D9C]/85">
                      <li className="flex items-start gap-2"><CheckIcon width={16} height={16} className="shrink-0 mt-0.5 text-[#2D8653]" /> Standard Certificate plus Personal ID Licence Card with photo</li>
                      <li className="flex items-start gap-2"><CheckIcon width={16} height={16} className="shrink-0 mt-0.5 text-[#2D8653]" /> Valid for three years — must be renewed</li>
                      <li className="flex items-start gap-2"><CheckIcon width={16} height={16} className="shrink-0 mt-0.5 text-[#2D8653]" /> New equipment can be added within the 3-year period</li>
                      <li className="flex items-start gap-2"><CheckIcon width={16} height={16} className="shrink-0 mt-0.5 text-[#2D8653]" /> Audited and approved by Competenz</li>
                      <li className="flex items-start gap-2"><CheckIcon width={16} height={16} className="shrink-0 mt-0.5 text-[#2D8653]" /> Exceeds NZ Department of Labour requirements</li>
                    </ul>
                  </div>
                </div>
              </div>
            </FadeInUp>

            <FadeInUp>
              <div className="callout-warning">
                <div className="label-eyebrow text-[#060D9C] mb-1">Renewals & WINZ</div>
                <p className="text-sm text-[#060D9C]/85">
                  A reduced rate applies for renewals of existing NFT certificates. WINZ quotations available on request — mention this when you enquire.
                </p>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>
    </>
  );
}
